import {
  Button,
  Checkbox,
  Heading,
  Paragraph,
  Select,
  Tag,
  Textarea,
} from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useId, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import {
  type AvtaleDto,
  getSearchAvtaler,
  type UgyldigAvtaleÅrsak,
} from "@/api/generated";
import { postUgyldigAvtaleMutation } from "@/api/generated/@tanstack/react-query.gen";
import FeilFjernvarmeBilde from "@/assets/feil-fjernvarme.webp?inline";
import {
  type AsyncSuggestionOption,
  AsyncSuggestions,
} from "@/components/AsyncSuggestions/AsyncSuggestions";
import { BestillingFormErrorSummary } from "@/components/forms/FormErrors";
import { FormWrapper } from "@/components/forms/FormWrapper";
import { KeyValueItem } from "@/components/KeyValueItem";
import {
  formatDateString,
  formatDateStringInterval,
} from "@/utils/dateTimeUtils";
import { useToaster } from "../toaster";
import {
  type FjernAvtaleFormSchema,
  fjernAvtaleFormSchema,
  initFormValues,
} from "./FjernAvtaleForm";

interface AvtaleSuggestionOption extends AsyncSuggestionOption, AvtaleDto {}

const searchAvtaler = async (
  organisasjonsNummer: number,
  query: string,
): Promise<{ data?: AvtaleSuggestionOption[]; error?: AxiosError }> => {
  try {
    const result = await getSearchAvtaler({
      client: httpClient,
      path: {
        organisasjonsNummer: organisasjonsNummer,
      },
      query: {
        searchString: query.trim(),
      },
      throwOnError: true,
    });

    if (result.data != null) {
      return {
        data: result.data.avtaler.map((a) => ({
          ...a,
          value: a.avtaleId,
          label: a.avtaleEier.navn,
          extraLabels: [
            { label: "AvtaleId", value: a.avtaleId },
            { label: "Kundenummer", value: a.kundenummer },
          ],
        })),
      };
    }

    return {
      error: new AxiosError(
        "Unknown error, result.data was null while searching for avtaler",
      ),
    };
  } catch (e) {
    if (e instanceof AxiosError) {
      return {
        error: e,
      };
    }

    return {
      error: new AxiosError("Unknown error while searching for avtaler"),
    };
  }
};

export const SøkAndFjernAvtaleForm = ({
  aktivLeverandør,
}: {
  aktivLeverandør: { navn: string; organisasjonsnummer: number };
}) => {
  const { t } = useTranslation();

  const [selectedAvtale, setSelectedAvtale] = useState<
    AvtaleSuggestionOption | undefined
  >(undefined);

  const onSelectedChanged = (
    avtaleOption: AvtaleSuggestionOption | undefined,
  ) => {
    setSelectedAvtale(avtaleOption);
  };

  const getOptions = (query: string) => {
    return searchAvtaler(aktivLeverandør.organisasjonsnummer, query);
  };

  const asyncSuggestionsKeyRef = useRef(0);

  const updateAsyncSuggestionsKey = () => {
    // Force rerender component to clear memory cached search results
    asyncSuggestionsKeyRef.current++;
  };

  return (
    <div className="max-w-card space-y-6 pt-6">
      <Heading level={2}>{t("pages.fjern-avtaler.form.søk-tittel")}</Heading>
      <AsyncSuggestions
        key={asyncSuggestionsKeyRef.current}
        title={t("pages.fjern-avtaler.form.søk-label")}
        description={t("pages.fjern-avtaler.form.søk-beskrivelse")}
        onSelectedChanged={onSelectedChanged}
        getOptions={getOptions}
      />
      {selectedAvtale && (
        <>
          <Heading>{t("pages.fjern-avtaler.form.tittel")}</Heading>
          <FjernAvtaleForm
            avtale={selectedAvtale}
            aktivLeverandør={aktivLeverandør}
            onAvtaleFjernet={updateAsyncSuggestionsKey}
          />
        </>
      )}
      {!selectedAvtale && (
        <div>
          <Heading level={3} data-size="xs">
            {t("pages.fjern-avtaler.tittel")}
          </Heading>
          <Paragraph className="mb-0">
            {t("pages.fjern-avtaler.beskrivelse1")}
          </Paragraph>
          <Paragraph>{t("pages.fjern-avtaler.beskrivelse2")}</Paragraph>
          {/* Legger på illustrasjon for å gi suggestion nok plass i høyden på mindre skjermer */}
          <img
            className="mx-auto max-w-card w-full pt-8"
            src={FeilFjernvarmeBilde}
            alt="Illustrasjon av hus med feil avtale"
          />
        </div>
      )}
    </div>
  );
};

const FjernAvtaleForm = ({
  avtale,
  aktivLeverandør,
  onAvtaleFjernet,
}: {
  avtale: AvtaleDto;
  aktivLeverandør: { navn: string; organisasjonsnummer: number };
  onAvtaleFjernet?: () => void;
}) => {
  const { t } = useTranslation();
  const toast = useToaster();
  const årsaker: UgyldigAvtaleÅrsak[] = ["Annet", "IngenKundeForhold"];
  const årsakFeltId = useId();
  const meldingTilBrukerFeltId = useId();
  const bekreftFeltId = useId();

  const defaultMeldingTilBruker = t(
    "pages.fjern-avtaler.form.melding-til-bruker-forslag",
    {
      navn: aktivLeverandør.navn,
    },
  );

  const mutation = useMutation({
    ...postUgyldigAvtaleMutation({
      client: httpClient,
      path: {
        organisasjonsNummer: aktivLeverandør.organisasjonsnummer,
      },
    }),
  });

  const form = useForm({
    resolver: zodResolver(fjernAvtaleFormSchema),
    values: initFormValues(defaultMeldingTilBruker),
    mode: "onSubmit",
  });

  const meldingTilBrukerLength = form.watch("meldingTilBruker").length;

  const [readonly, setReadonly] = useState(
    () => avtale.avtaleStatus === "Ugyldig",
  );

  useEffect(() => {
    setReadonly(avtale.avtaleStatus === "Ugyldig");
    form.reset();
  }, [avtale, form.reset]);

  async function handleSubmit(values: FjernAvtaleFormSchema) {
    await mutation
      .mutateAsync({
        body: {
          avtaleId: avtale.avtaleId,
          årsak: values.årsak,
          kommentar: values.meldingTilBruker,
        },
        path: {
          organisasjonsNummer: aktivLeverandør.organisasjonsnummer,
        },
      })
      .then(async (_) => {
        toast.success({ messageKey: "pages.fjern-avtaler.form.toast.lagret" });
        setReadonly(true);
        onAvtaleFjernet?.();
      })
      .catch((error) => {
        console.error("Feil ved fjerning av avtale", error);
        toast.error({ messageKey: "pages.fjern-avtaler.form.toast.feil" });
      });
  }

  return (
    <FormProvider {...form}>
      <FormWrapper onSubmit={form.handleSubmit(handleSubmit)}>
        <div>
          {readonly && (
            <Tag data-color="warning" className="mb-2">
              {t("pages.fjern-avtaler.form.ugyldig-tag")}
            </Tag>
          )}
          <Heading level={2} data-size="md">
            {t("pages.fjern-avtaler.form.innsender", {
              navn: avtale.bestiller.navn,
            })}
          </Heading>
          <span className="text-sm">
            {t("pages.fjern-avtaler.form.bestillingstidspunkt", {
              date: formatDateString(avtale.createdAt, "short", true),
            })}
          </span>
        </div>
        <div>
          <KeyValueItem
            label={t("pages.fjern-avtaler.form.avtaleId")}
            value={avtale.avtaleId}
          />
          <KeyValueItem
            label={t("pages.fjern-avtaler.form.kundenummer")}
            value={avtale.kundenummer}
          />
          <KeyValueItem
            label={t("pages.fjern-avtaler.form.avtaleperiode")}
            value={formatDateStringInterval(
              avtale.activeFromInclusive,
              avtale.activeToExclusive,
            )}
          />
          <KeyValueItem
            label={t("pages.fjern-avtaler.form.type-bolig")}
            value={
              avtale.avtaleType === "Privat"
                ? avtale.avtaleDetaljerPrivat?.boligType
                : avtale.avtaleDetaljerSelskap?.boligType
            }
          />
          <KeyValueItem
            label={t("pages.fjern-avtaler.form.målepunkt-id")}
            value={
              avtale.målerId ??
              t("pages.fjern-avtaler.form.målepunkt-id-ikke-satt")
            }
          />
          {avtale.avtaleType === "Privat" && (
            <KeyValueItem
              label={t("pages.fjern-avtaler.form.adresse")}
              value={avtale.avtaleDetaljerPrivat?.adresse}
            />
          )}
        </div>
        <div className="space-y-2">
          <Heading level={3} data-size="xs">
            {t("pages.fjern-avtaler.form.velg-årsak")}
          </Heading>
          <Select
            id={årsakFeltId}
            {...form.register("årsak")}
            readOnly={readonly}
          >
            {årsaker.map((årsak) => (
              <Select.Option key={årsak} value={årsak}>
                {t("pages.fjern-avtaler.statuser.ugyldigAvtaleÅrsak", {
                  context: årsak,
                })}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="space-y-2">
          <div>
            <Heading level={3} data-size="xs">
              {t("pages.fjern-avtaler.form.melding-til-bruker")}
            </Heading>
            <span className="text-balder-text-subtle">
              {t("pages.fjern-avtaler.form.melding-til-bruker-beskrivelse")}
            </span>
          </div>
          <div>
            <Textarea
              id={meldingTilBrukerFeltId}
              {...form.register("meldingTilBruker")}
              maxLength={1000}
              readOnly={readonly}
            />
            {!readonly && (
              <span className="text-balder-text-subtle">
                {t("pages.fjern-avtaler.form.tegn-igjen", {
                  antall: `${1000 - meldingTilBrukerLength}/${1000}`,
                })}
              </span>
            )}
          </div>
        </div>
        {!readonly && (
          <>
            <BestillingFormErrorSummary errors={form.formState.errors} />
            <div className="space-y-4">
              <Heading level={3} data-size="xs">
                {t("pages.fjern-avtaler.form.bekreft-fjerning")}
              </Heading>
              <Checkbox
                id={bekreftFeltId}
                {...form.register("bekreftelse")}
                label={t("pages.fjern-avtaler.form.bekreft-checkbox")}
              />
              <Button
                variant="primary"
                data-color="danger"
                type="submit"
                loading={form.formState.isSubmitting || mutation.isPending}
                disabled={form.formState.isSubmitting || mutation.isPending}
              >
                {t("pages.fjern-avtaler.form.fjern-avtale-knapp")}
              </Button>
            </div>
          </>
        )}
      </FormWrapper>
    </FormProvider>
  );
};
